import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, FileText, Clock, Users, Send, CheckSquare, CheckCircle, MessageSquare, History, Pencil, Copy, RefreshCw, UserMinus, Link2, Database, CircleCheck, CircleAlert, CircleX, CircleHelp, Eye, AlertTriangle, Check, RotateCw } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pagination } from '@/components/ui/pagination';
import { InviteChannel, LegalForm, AccountingType, VatRegime, DocumentStatus, DocumentType, ExpenseCategory } from '@taxistry/types';
import type { Document as TaxistryDocument } from '@taxistry/types';

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  [DocumentType.TIMOLOGIO_POLISIS]: 'Τιμολόγιο Πώλησης',
  [DocumentType.TIMOLOGIO_PAROHIS_YPIRESION]: 'Τιμολόγιο Παροχής Υπηρεσιών',
  [DocumentType.APODEIXI]: 'Απόδειξη',
  [DocumentType.PISTOTIKO]: 'Πιστωτικό',
  [DocumentType.LOGARIASMOS_DEKO]: 'Λογαριασμός ΔΕΚΟ',
  [DocumentType.APODEIXI_ENOIKIOU]: 'Απόδειξη Ενοικίου',
  [DocumentType.OTHER]: 'Άλλο',
};

const EXPENSE_CATEGORY_LABELS: Record<string, string> = {
  [ExpenseCategory.TILEKOINONIES]: 'Τηλεπικοινωνίες',
  [ExpenseCategory.ENOIKIA]: 'Ενοίκια',
  [ExpenseCategory.YLIKA]: 'Υλικά',
  [ExpenseCategory.METAFORIKA]: 'Μεταφορικά',
  [ExpenseCategory.KAFETERIA_ESTIASI]: 'Καφετέρια / Εστίαση',
  [ExpenseCategory.LOGISTIKES_YPIRESIES]: 'Λογιστικές Υπηρεσίες',
  [ExpenseCategory.ENERGIA]: 'Ενέργεια',
  [ExpenseCategory.YDREFSI]: 'Ύδρευση',
  [ExpenseCategory.MISTHODOSIA]: 'Μισθοδοσία',
  [ExpenseCategory.ASFALISTIKES_EISFORES]: 'Ασφαλιστικές Εισφορές',
  [ExpenseCategory.OTHER]: 'Άλλο',
};
import {
  useClient, useUpdateClient, useContacts, useCreateContact, useDeactivateContact,
  useDocuments, useDocument, useUpdateDocumentStatus, useConfirmExtraction, useReprocessDocument, useDeadlines,
  useGenerateDeadlines, useActivity, useAddNote, useClientRequests,
  useCreateClientRequest, useUpdateClientRequest, useClientOnboarding,
  useOnboardingTemplates, useCreateOnboardingChecklist, useUpdateOnboardingChecklist,
  useUsers,
  useMyDataSyncs, useMyDataInvoices, useMyDataInvoice, useInitMyDataSync, useTriggerMyDataSync,
  useMessageThreads, useMessageThread, useCreateThread, useSendMessage, useMarkThreadRead,
} from '@/hooks/queries';
import { api } from '@/lib/api';

export function ClientProfilePage({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const { data: client, isLoading } = useClient(clientId);
  const { data: contactsData } = useContacts(clientId);
  const [editOpen, setEditOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Ο πελάτης δεν βρέθηκε.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header — breadcrumb style */}
      <div>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
          <Link to="/clients" className="hover:text-foreground transition-colors">
            <ArrowLeft className="h-3.5 w-3.5 inline mr-1" />
            {t('clients.title')}
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">{client.businessName}</span>
        </div>
        <div className="flex items-center gap-3">
          <h1 className="font-display text-xl font-bold">{client.businessName}</h1>
          <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Επεξεργασία
          </Button>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline">{t(`clients.legalForms.${client.legalForm}`)}</Badge>
          <Badge variant="secondary">{t(`clients.vatRegimes.${client.vatRegime}`)}</Badge>
          <span className="text-sm text-muted-foreground font-data">{t('clients.afm')}: {client.afm}</span>
          {(client.tags ?? []).map((tag) => (
            <Badge key={tag} variant="outline" className="text-xs bg-primary/10 border-primary/20 text-primary">{tag}</Badge>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-border rounded-[13px] bg-surface-2 relative overflow-hidden">
          <CardContent className="p-3">
            <p className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-bold mb-1">{t('documents.title')}</p>
            <p className="text-[26px] font-bold leading-tight">{client.documentsCount ?? 0}</p>
            <FileText className="absolute right-4 bottom-3 h-10 w-10 text-blue-600 opacity-[0.07]" />
          </CardContent>
        </Card>
        <Card className="border border-border rounded-[13px] bg-surface-2 relative overflow-hidden">
          <CardContent className="p-3">
            <p className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-bold mb-1">Επόμενη προθεσμία</p>
            <p className="text-[26px] font-bold leading-tight">
              {client.nextDeadline
                ? <span className="font-data">{new Date(client.nextDeadline.dueDate).toLocaleDateString('el-GR')}</span>
                : '—'}
            </p>
            <Clock className="absolute right-4 bottom-3 h-10 w-10 text-amber-600 opacity-[0.07]" />
          </CardContent>
        </Card>
        <Card className="border border-border rounded-[13px] bg-surface-2 relative overflow-hidden">
          <CardContent className="p-3">
            <p className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground font-bold mb-1">{t('clients.contacts')}</p>
            <p className="text-[26px] font-bold leading-tight">{client.contactsCount ?? 0}</p>
            <Users className="absolute right-4 bottom-3 h-10 w-10 text-primary opacity-[0.07]" />
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Στοιχεία</TabsTrigger>
          <TabsTrigger value="documents">{t('documents.title')}</TabsTrigger>
          <TabsTrigger value="deadlines">{t('deadlines.title')}</TabsTrigger>
          <TabsTrigger value="contacts">{t('clients.contacts')}</TabsTrigger>
          <TabsTrigger value="mydata">myDATA</TabsTrigger>
          <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="staff">Ομάδα</TabsTrigger>
          <TabsTrigger value="messages">Μηνύματα</TabsTrigger>
          <TabsTrigger value="requests">Αιτήματα</TabsTrigger>
          <TabsTrigger value="activity">Ιστορικό</TabsTrigger>
        </TabsList>

        {/* Overview tab */}
        <TabsContent value="overview">
          <Card className="rounded-2xl border-border shadow-lifted">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-8 text-sm">
                <InfoField label={t('clients.businessName')} value={client.businessName} />
                <InfoField label={t('clients.afm')} value={client.afm} mono />
                <InfoField label={t('clients.legalForm')} value={t(`clients.legalForms.${client.legalForm}`)} />
                <InfoField label="Λογιστικό Σύστημα" value={client.accountingType === 'SINGLE_ENTRY' ? 'Απλογραφικά' : 'Διπλογραφικά'} />
                <InfoField label={t('clients.vatRegime')} value={t(`clients.vatRegimes.${client.vatRegime}`)} />
                <InfoField label={t('clients.sector')} value={client.sector ?? '—'} />
                <InfoField label="ΚΑΔ" value={client.kadCode ?? '—'} />
                <InfoField label="Εργαζόμενοι" value={client.hasEmployees ? 'Ναι' : 'Όχι'} />
                <InfoField label="Ενοίκια" value={client.hasRentalIncome ? 'Ναι' : 'Όχι'} />
                <InfoField label="Ακίνητα" value={client.hasProperty ? 'Ναι' : 'Όχι'} />
              </div>
              {client.notes && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium mb-1">Σημειώσεις</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{client.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents tab */}
        <TabsContent value="documents">
          <ClientDocumentsTab clientId={clientId} />
        </TabsContent>

        {/* Deadlines tab */}
        <TabsContent value="deadlines">
          <DeadlinesTab clientId={clientId} />
        </TabsContent>

        {/* Contacts tab */}
        <TabsContent value="contacts">
          <ContactsTab clientId={clientId} contactsData={contactsData} />
        </TabsContent>

        {/* myDATA tab */}
        <TabsContent value="mydata">
          <MyDataTab clientId={clientId} />
        </TabsContent>

        {/* Onboarding tab */}
        <TabsContent value="onboarding">
          <OnboardingTab clientId={clientId} />
        </TabsContent>

        {/* Staff tab (B-7) */}
        <TabsContent value="staff">
          <StaffAssignmentTab clientId={clientId} />
        </TabsContent>

        {/* Messages tab */}
        <TabsContent value="messages">
          <ClientMessagesTab clientId={clientId} />
        </TabsContent>

        {/* Requests tab */}
        <TabsContent value="requests">
          <RequestsTab clientId={clientId} />
        </TabsContent>

        {/* Activity tab */}
        <TabsContent value="activity">
          <ActivityTab clientId={clientId} />
        </TabsContent>
      </Tabs>

      {/* Edit Client Dialog (B-1) */}
      {editOpen && client && (
        <EditClientDialog
          client={client}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}

// ─── myDATA Tab ───────────────────────────────

function MyDataTab({ clientId }: { clientId: string }) {
  const { data: syncsData } = useMyDataSyncs({ clientId, limit: 1 });
  const [invPage, setInvPage] = useState(1);
  const [invLimit, setInvLimit] = useState(10);
  const { data: invoicesData } = useMyDataInvoices({ clientId, page: invPage, limit: invLimit });
  const initSync = useInitMyDataSync();
  const triggerSync = useTriggerMyDataSync();
  const [invoiceDir, setInvoiceDir] = useState<string>('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const sync = syncsData?.data?.[0];
  const invoices = invoicesData?.data ?? [];

  // Filter invoices by direction
  const filtered = invoiceDir
    ? invoices.filter((inv) => inv.direction === invoiceDir)
    : invoices;

  const statusIcon = (s: string) => {
    switch (s) {
      case 'GREEN': return <CircleCheck className="h-4 w-4 text-green-500" />;
      case 'YELLOW': return <CircleAlert className="h-4 w-4 text-amber-500" />;
      case 'RED': return <CircleX className="h-4 w-4 text-red-500" />;
      default: return <CircleHelp className="h-4 w-4 text-gray-400" />;
    }
  };

  if (!sync) {
    return (
      <Card>
        <CardContent className="py-8 text-center space-y-3">
          <Database className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">
            Ο συγχρονισμός myDATA δεν έχει ενεργοποιηθεί για αυτόν τον πελάτη.
          </p>
          <Button
            size="sm"
            onClick={() => initSync.mutate(clientId)}
            disabled={initSync.isPending}
          >
            Ενεργοποίηση myDATA
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sync status card */}
      <Card>
        <CardHeader className="flex-row items-center justify-between pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            {statusIcon(sync.overallStatus)}
            myDATA — {sync.overallStatus === 'GREEN' ? 'OK' : sync.overallStatus === 'YELLOW' ? 'Εκκρεμεί' : sync.overallStatus === 'RED' ? 'Πρόβλημα' : 'Άγνωστο'}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => triggerSync.mutate(clientId)}
            disabled={triggerSync.isPending}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${triggerSync.isPending ? 'animate-spin' : ''}`} />
            Συγχρονισμός
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-[11px] uppercase tracking-[0.06em] font-bold mb-0.5">Εκδοθέντα</p>
              <p className="font-bold text-lg">{sync.transmittedCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-[11px] uppercase tracking-[0.06em] font-bold mb-0.5">Ληφθέντα</p>
              <p className="font-bold text-lg">{sync.receivedCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-[11px] uppercase tracking-[0.06em] font-bold mb-0.5">Εκκρ. Χαρακτηρισμοί</p>
              <p className={`font-bold text-lg ${sync.pendingClassificationCount > 0 ? 'text-amber-600' : ''}`}>
                {sync.pendingClassificationCount}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground text-[11px] uppercase tracking-[0.06em] font-bold mb-0.5">Απορρίψεις</p>
              <p className={`font-bold text-lg ${sync.rejectedCount > 0 ? 'text-red-600' : ''}`}>
                {sync.rejectedCount}
              </p>
            </div>
          </div>
          {sync.lastError && (
            <div className="mt-3 p-2 bg-red-50 rounded text-sm text-red-700">
              Σφάλμα: {sync.lastError}
            </div>
          )}
          {sync.lastPolledAt && (
            <p className="text-xs text-muted-foreground mt-3">
              Τελ. ενημέρωση: {new Date(sync.lastPolledAt).toLocaleString('el-GR')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Invoices list */}
      <Card>
        <CardHeader className="flex-row items-center justify-between pb-3">
          <CardTitle className="font-display text-base">Παραστατικά myDATA</CardTitle>
          <Select value={invoiceDir || 'ALL'} onValueChange={(v) => setInvoiceDir(v === 'ALL' ? '' : v)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Όλα</SelectItem>
              <SelectItem value="ISSUED">Εκδοθέντα</SelectItem>
              <SelectItem value="RECEIVED">Ληφθέντα</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>MARK</TableHead>
              <TableHead>Κατεύθυνση</TableHead>
              <TableHead>Τύπος</TableHead>
              <TableHead>Ημερομηνία</TableHead>
              <TableHead>Αντισυμβαλλόμενος</TableHead>
              <TableHead className="text-right">Αξία</TableHead>
              <TableHead>Κατάσταση</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Δεν βρέθηκαν παραστατικά
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((inv) => (
                <TableRow
                  key={inv.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSelectedInvoiceId(inv.id)}
                >
                  <TableCell className="font-data text-xs">{inv.mark}</TableCell>
                  <TableCell>
                    <Badge variant={inv.direction === 'ISSUED' ? 'default' : 'secondary'} className="text-xs">
                      {inv.direction === 'ISSUED' ? 'Έκδοση' : 'Λήψη'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {inv.invoiceType ? (INVOICE_TYPE_LABELS[inv.invoiceType] || inv.invoiceType) : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('el-GR') : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>
                      {inv.direction === 'ISSUED'
                        ? (inv.counterpartName || inv.counterpartAfm || '—')
                        : (inv.issuerName || inv.issuerAfm || '—')}
                    </div>
                    <div className="text-xs text-muted-foreground font-data">
                      {inv.direction === 'ISSUED' ? inv.counterpartAfm : inv.issuerAfm}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-data text-sm">
                    {inv.totalValue ? `${Number(inv.totalValue).toLocaleString('el-GR', { minimumFractionDigits: 2 })} €` : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {inv.cancelled ? (
                        <Badge variant="secondary" className="text-xs">Ακυρωμένο</Badge>
                      ) : null}
                      {inv.hasRejection ? (
                        <Badge variant="destructive" className="text-xs">Απόρριψη</Badge>
                      ) : null}
                      {inv.hasGap ? (
                        <Badge variant="warning" className="text-xs">Κενό</Badge>
                      ) : null}
                      {!inv.cancelled && !inv.hasRejection && !inv.hasGap ? (
                        inv.incomeClassified || inv.expenseClassified
                          ? <Badge variant="success" className="text-xs">Χαρ/σμένο</Badge>
                          : <Badge variant="secondary" className="text-xs">Εκκρεμεί</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {invoicesData && (
          <div className="px-4 py-3 border-t">
            <Pagination
              page={invoicesData.page}
              totalPages={invoicesData.totalPages}
              total={invoicesData.total}
              limit={invLimit}
              onPageChange={setInvPage}
              onLimitChange={setInvLimit}
            />
          </div>
        )}
      </Card>

      {/* Invoice Detail Dialog */}
      {selectedInvoiceId && (
        <InvoiceDetailDialog
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
        />
      )}
    </div>
  );
}

// ─── Invoice Detail Dialog ────────────────────

const INVOICE_TYPE_LABELS: Record<string, string> = {
  '1.1': 'Τιμολόγιο Πώλησης',
  '1.2': 'Τιμολόγιο Πώλησης / Ενδοκοινοτικές',
  '1.3': 'Τιμολόγιο Πώλησης / Τρίτες Χώρες',
  '1.4': 'Τιμολόγιο Πώλησης / Συμπληρωματικό',
  '1.5': 'Τιμολόγιο Πώλησης / Πιστωτικό',
  '1.6': 'Τιμολόγιο Πώλησης / Στοιχείο Αυτοπαράδοσης',
  '2.1': 'Τιμολόγιο Παροχής Υπηρεσιών',
  '2.2': 'Τιμολόγιο Παροχής / Ενδοκοινοτικές',
  '2.3': 'Τιμολόγιο Παροχής / Τρίτες Χώρες',
  '2.4': 'Τιμολόγιο Παροχής / Συμπληρωματικό',
  '3.1': 'Τίτλος Κτήσης',
  '3.2': 'Τίτλος Κτήσης (Αρνητικό)',
  '5.1': 'Πιστωτικό Τιμολόγιο',
  '5.2': 'Πιστωτικό / Ενδοκοινοτικές',
  '6.1': 'Στοιχείο Αυτοπαράδοσης',
  '6.2': 'Στοιχείο Ιδιοχρησιμοποίησης',
  '7.1': 'Συμβόλαιο - Έσοδο',
  '8.1': 'Ενοίκια - Έσοδο',
  '8.2': 'Ειδικό Στοιχείο (Απόδειξη Είσπραξης)',
  '11.1': 'Απόδειξη Λιανικής',
  '11.2': 'Απόδειξη Παροχής Υπηρεσιών',
  '11.3': 'Απλοποιημένο Τιμολόγιο',
  '11.4': 'Πιστωτικό Λιανικής',
  '11.5': 'Απόδειξη Λιανικής (για λ/σμό τρίτων)',
};

function fmtAmount(value: string | null): string {
  if (!value) return '—';
  return `${Number(value).toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function InvoiceDetailDialog({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const { data: inv, isLoading } = useMyDataInvoice(invoiceId);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Παραστατικό myDATA
          </DialogTitle>
          {inv && (
            <DialogDescription className="font-data">
              MARK: {inv.mark}
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : inv ? (
          <div className="space-y-5">
            {/* Status badges */}
            <div className="flex flex-wrap gap-1.5">
              <Badge variant={inv.direction === 'ISSUED' ? 'default' : 'secondary'}>
                {inv.direction === 'ISSUED' ? 'Έκδοση' : 'Λήψη'}
              </Badge>
              {inv.cancelled ? (
                <Badge variant="secondary">Ακυρωμένο</Badge>
              ) : null}
              {inv.hasRejection ? (
                <Badge variant="destructive">Απόρριψη</Badge>
              ) : null}
              {inv.hasGap ? (
                <Badge variant="warning">Κενό αρίθμησης</Badge>
              ) : null}
              {inv.incomeClassified ? (
                <Badge variant="success">Χαρ. Εσόδων</Badge>
              ) : null}
              {inv.expenseClassified ? (
                <Badge variant="success">Χαρ. Εξόδων</Badge>
              ) : null}
              {!inv.cancelled && !inv.incomeClassified && !inv.expenseClassified ? (
                <Badge variant="secondary">Εκκρ. Χαρακτηρισμού</Badge>
              ) : null}
            </div>

            {/* Invoice identification */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground">Τύπος</p>
                <p>{inv.invoiceType ? (INVOICE_TYPE_LABELS[inv.invoiceType] || inv.invoiceType) : '—'}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground">Ημ/νία Έκδοσης</p>
                <p>{inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('el-GR') : '—'}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground">Σειρά</p>
                <p className="font-data">{inv.series || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground">Αριθμός (ΑΑ)</p>
                <p className="font-data">{inv.aa || '—'}</p>
              </div>
              {inv.uid ? (
                <div className="col-span-2">
                  <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground">UID</p>
                  <p className="font-data text-xs break-all">{inv.uid}</p>
                </div>
              ) : null}
            </div>

            {/* Parties */}
            <div className="border border-border rounded-md p-3 space-y-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground mb-0.5">Εκδότης</p>
                <p className="text-sm font-medium">{inv.issuerName || '—'}</p>
                {inv.issuerAfm ? (
                  <p className="text-xs text-muted-foreground font-data">ΑΦΜ: {inv.issuerAfm}</p>
                ) : null}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground mb-0.5">Αντισυμβαλλόμενος</p>
                <p className="text-sm font-medium">{inv.counterpartName || '—'}</p>
                {inv.counterpartAfm ? (
                  <p className="text-xs text-muted-foreground font-data">ΑΦΜ: {inv.counterpartAfm}</p>
                ) : null}
              </div>
            </div>

            {/* Amounts */}
            <div className="border border-border rounded-md p-3">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground mb-0.5">Καθαρή Αξία</p>
                  <p className="font-data">{fmtAmount(inv.netValue)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground mb-0.5">ΦΠΑ</p>
                  <p className="font-data">{fmtAmount(inv.vatAmount)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.06em] font-bold text-muted-foreground mb-0.5">Σύνολο</p>
                  <p className="font-data font-bold">{fmtAmount(inv.totalValue)}</p>
                </div>
              </div>
            </div>

            {/* Cancellation info */}
            {inv.cancelled && inv.cancellationMark ? (
              <div className="p-3 bg-muted rounded-md text-sm">
                <p className="text-muted-foreground">
                  Ακυρωτικό MARK: <span className="font-data">{inv.cancellationMark}</span>
                </p>
              </div>
            ) : null}

            {/* Rejection explanations */}
            {inv.rejectionExplanations && inv.rejectionExplanations.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium flex items-center gap-1.5 text-red-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Σφάλματα / Απορρίψεις
                </p>
                {inv.rejectionExplanations.map((rej, i) => (
                  <div key={i} className="p-3 bg-red-50 rounded-md text-sm space-y-1">
                    <p className="font-medium text-red-800">
                      Κωδικός {rej.code}: {rej.explanation}
                    </p>
                    {rej.originalMessage ? (
                      <p className="text-red-600 text-xs font-data">{rej.originalMessage}</p>
                    ) : null}
                    <p className="text-red-700 text-xs">{rej.suggestion}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {/* Timestamps */}
            <div className="flex gap-6 text-xs text-muted-foreground pt-1 border-t">
              <p>Εισαγωγή: {new Date(inv.createdAt).toLocaleString('el-GR')}</p>
              <p>Ενημέρωση: {new Date(inv.updatedAt).toLocaleString('el-GR')}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">Το παραστατικό δεν βρέθηκε.</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Κλείσιμο</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helper Components ────────────────────────

function InfoField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-muted-foreground text-[11px] uppercase tracking-[0.06em] font-bold mb-0.5">{label}</p>
      <p className={`font-medium ${mono ? 'font-data' : ''}`}>{value}</p>
    </div>
  );
}

function DeadlinesTab({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const generateDeadlines = useGenerateDeadlines();
  const { data: deadlinesData } = useDeadlines({ clientId, page, limit, sortBy: 'dueDate', sortOrder: 'asc' });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-3">
        <CardTitle className="font-display text-base">{t('deadlines.title')}</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => generateDeadlines.mutate({ clientId, year: new Date().getFullYear() })}
          disabled={generateDeadlines.isPending}
        >
          Δημιουργία Προθεσμιών {new Date().getFullYear()}
        </Button>
      </CardHeader>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Υποχρέωση</TableHead>
            <TableHead>Περίοδος</TableHead>
            <TableHead>{t('deadlines.dueDate')}</TableHead>
            <TableHead>Κατάσταση</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!deadlinesData?.data.length ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                {t('deadlines.noDeadlines')}
              </TableCell>
            </TableRow>
          ) : (
            deadlinesData.data.map((dl) => {
              const s = dl.status as string;
              const statusVariant = s === 'OVERDUE' ? 'error'
                : s === 'FILED' || s === 'CONFIRMED' ? 'success'
                : s === 'IN_PROGRESS' ? 'default'
                : s === 'BLOCKED' ? 'error'
                : s === 'PENDING' ? 'warning'
                : s === 'SKIPPED' ? 'secondary'
                : 'secondary';
              return (
                <TableRow key={dl.id}>
                  <TableCell className="font-medium">{dl.obligationNameEl ?? dl.obligationCode ?? '—'}</TableCell>
                  <TableCell>{dl.periodLabel}</TableCell>
                  <TableCell><span className="font-data">{new Date(dl.dueDate).toLocaleDateString('el-GR')}</span></TableCell>
                  <TableCell>
                    <Badge variant={statusVariant}>{t(`deadlines.status.${dl.status}`)}</Badge>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {deadlinesData && (
        <div className="px-4 py-3 border-t">
          <Pagination
            page={deadlinesData.page}
            totalPages={deadlinesData.totalPages}
            total={deadlinesData.total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      )}
    </Card>
  );
}

function ContactsTab({
  clientId,
  contactsData,
}: {
  clientId: string;
  contactsData: ReturnType<typeof useContacts>['data'];
}) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const deactivateContact = useDeactivateContact();
  const [regenerating, setRegenerating] = useState<string | null>(null);

  const portalBaseUrl = (typeof window !== 'undefined' ? window.location.origin : '').replace(':5173', ':5174');

  const handleRegenerate = async (contactId: string) => {
    setRegenerating(contactId);
    try {
      await api.post(`/contacts/${contactId}/regenerate-link`, { resend: true });
    } finally {
      setRegenerating(null);
    }
  };

  const handleCopyLink = (token: string) => {
    const url = `${portalBaseUrl}/portal?token=${token}`;
    navigator.clipboard.writeText(url);
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-3">
        <CardTitle className="font-display text-base">{t('clients.contacts')}</CardTitle>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Προσθήκη Επαφής
            </Button>
          </DialogTrigger>
          <CreateContactDialog clientId={clientId} onClose={() => setDialogOpen(false)} />
        </Dialog>
      </CardHeader>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Όνομα</TableHead>
            <TableHead>Τηλέφωνο</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Ρόλος</TableHead>
            <TableHead>Κατάσταση</TableHead>
            <TableHead>Τελευταίο Upload</TableHead>
            <TableHead>Ενέργειες</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!contactsData?.data.length ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                Δεν υπάρχουν επαφές
              </TableCell>
            </TableRow>
          ) : (
            contactsData.data.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell className="font-medium">{contact.name}</TableCell>
                <TableCell>{contact.phone ?? '—'}</TableCell>
                <TableCell>{contact.email ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground">{contact.roleLabel ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={contact.isActive ? 'success' : 'secondary'}>
                    {contact.isActive ? 'Ενεργή' : 'Ανενεργή'}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {contact.lastUploadAt
                    ? new Date(contact.lastUploadAt).toLocaleDateString('el-GR')
                    : '—'}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {contact.isActive && contact.magicToken ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Αντιγραφή συνδέσμου"
                          onClick={() => handleCopyLink(contact.magicToken!)}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Ανανέωση συνδέσμου"
                          disabled={regenerating === contact.id}
                          onClick={() => handleRegenerate(contact.id)}
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${regenerating === contact.id ? 'animate-spin' : ''}`} />
                        </Button>
                      </>
                    ) : null}
                    {contact.isActive ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-700"
                        title="Απενεργοποίηση"
                        onClick={() => deactivateContact.mutate(contact.id)}
                      >
                        <UserMinus className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

// ─── Onboarding Tab ──────────────────────────

function OnboardingTab({ clientId }: { clientId: string }) {
  const { data: checklistData } = useClientOnboarding(clientId);
  const { data: templatesData } = useOnboardingTemplates();
  const createChecklist = useCreateOnboardingChecklist();
  const updateChecklist = useUpdateOnboardingChecklist();
  const [showTemplateSelect, setShowTemplateSelect] = useState(false);

  const checklists = checklistData?.data ?? [];
  const templates = templatesData?.data ?? [];

  function handleToggle(checklistId: string, itemsStatus: Record<string, boolean>, key: string) {
    updateChecklist.mutate({
      id: checklistId,
      itemsStatus: { ...itemsStatus, [key]: !itemsStatus[key] },
    });
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-3">
        <CardTitle className="font-display text-base flex items-center gap-2">
          <CheckSquare className="h-4 w-4" />
          Onboarding
        </CardTitle>
        {checklists.length === 0 && (
          showTemplateSelect ? (
            <div className="flex items-center gap-2">
              <Select onValueChange={(templateId) => {
                createChecklist.mutate({ clientId, templateId });
                setShowTemplateSelect(false);
              }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Επιλογή template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="ghost" onClick={() => setShowTemplateSelect(false)}>Ακύρωση</Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => setShowTemplateSelect(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Έναρξη Onboarding
            </Button>
          )
        )}
      </CardHeader>
      <CardContent>
        {checklists.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Δεν υπάρχει checklist onboarding. Ξεκινήστε ένα από template.
          </p>
        ) : (
          checklists.map((cl) => {
            const items = cl.items ?? [];
            const status = cl.itemsStatus ?? {};
            const completedCount = Object.values(status).filter(Boolean).length;
            const progress = items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0;

            return (
              <div key={cl.id} className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{cl.templateName}</p>
                  <Badge variant={cl.completedAt ? 'success' : 'secondary'}>
                    {cl.completedAt ? 'Ολοκληρώθηκε' : `${progress}%`}
                  </Badge>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="space-y-1">
                  {items.map((item) => (
                    <label
                      key={item.key}
                      className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-muted/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={!!status[item.key]}
                        onChange={() => handleToggle(cl.id, status, item.key)}
                        className="rounded border-border"
                      />
                      <span className={`text-sm ${status[item.key] ? 'text-muted-foreground line-through' : ''}`}>
                        {item.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

// ─── Messages Tab ────────────────────────────

function ClientMessagesTab({ clientId }: { clientId: string }) {
  const [threadPage, setThreadPage] = useState(1);
  const [threadLimit, setThreadLimit] = useState(10);
  const { data: threadsData, isLoading } = useMessageThreads({ clientId, page: threadPage, limit: threadLimit });
  const createThread = useCreateThread();
  const [newBody, setNewBody] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);

  const threads = threadsData?.data ?? [];

  const handleCreate = () => {
    if (!newBody.trim()) return;
    createThread.mutate(
      { clientId, subject: newSubject.trim() || undefined, body: newBody.trim() },
      {
        onSuccess: (data: any) => {
          setNewBody('');
          setNewSubject('');
          setShowNew(false);
          setOpenThreadId(data.id);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Μηνύματα
          </CardTitle>
          <Button size="sm" onClick={() => setShowNew(!showNew)}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Νέο Νήμα
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showNew && (
          <div className="mb-4 p-3 border border-border rounded-lg space-y-2 bg-muted/30">
            <Input
              placeholder="Θέμα (προαιρετικό)"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
            />
            <Textarea
              placeholder="Γράψτε μήνυμα..."
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowNew(false)}>Ακύρωση</Button>
              <Button size="sm" onClick={handleCreate} disabled={!newBody.trim() || createThread.isPending}>
                <Send className="w-3.5 h-3.5 mr-1" />
                Αποστολή
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : threads.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Δεν υπάρχουν μηνύματα</p>
        ) : (
          <div className="divide-y">
            {threads.map((thread) => {
              const isUnread =
                thread.lastMessageAt &&
                (!thread.lastOfficeReadAt || thread.lastMessageAt > thread.lastOfficeReadAt);
              const isOpen = openThreadId === thread.id;
              return (
                <div key={thread.id}>
                  <button
                    onClick={() => setOpenThreadId(isOpen ? null : thread.id)}
                    className={`w-full text-left py-2.5 flex items-center justify-between hover:bg-muted/30 transition-colors px-1 rounded ${isOpen ? 'bg-muted/30' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {isUnread ? <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" /> : null}
                        <span className={`text-sm truncate ${isUnread ? 'font-semibold' : ''}`}>
                          {thread.subject || 'Χωρίς θέμα'}
                        </span>
                      </div>
                      {!isOpen && thread.lastMessagePreview ? (
                        <p className="text-xs text-muted-foreground truncate mt-0.5 pl-3.5">{thread.lastMessagePreview}</p>
                      ) : null}
                    </div>
                    {thread.lastMessageAt ? (
                      <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                        {new Date(thread.lastMessageAt).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' })}
                      </span>
                    ) : null}
                  </button>
                  {isOpen && (
                    <ThreadDetail threadId={thread.id} />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {threadsData && (
          <div className="pt-3 border-t mt-4">
            <Pagination
              page={threadsData.page}
              totalPages={threadsData.totalPages}
              total={threadsData.total}
              limit={threadLimit}
              onPageChange={setThreadPage}
              onLimitChange={setThreadLimit}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ThreadDetail({ threadId }: { threadId: string }) {
  const { data: threadData, isLoading } = useMessageThread(threadId);
  const markRead = useMarkThreadRead();
  const sendMessage = useSendMessage();
  const [reply, setReply] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const threadMessages = threadData?.messages?.data ?? [];

  // Mark as read on open
  useEffect(() => {
    if (threadData) {
      const isUnread =
        threadData.lastMessageAt &&
        (!threadData.lastOfficeReadAt || threadData.lastMessageAt > threadData.lastOfficeReadAt);
      if (isUnread) {
        markRead.mutate(threadId);
      }
    }
  }, [threadId, threadData?.lastMessageAt]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadMessages.length]);

  const handleSend = () => {
    if (!reply.trim()) return;
    sendMessage.mutate(
      { threadId, body: reply.trim() },
      { onSuccess: () => setReply('') },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isLoading) {
    return (
      <div className="p-3 space-y-2">
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-10 w-1/2" />
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg mb-2 bg-background">
      {/* Messages */}
      <div className="max-h-80 overflow-y-auto p-3 space-y-3">
        {threadMessages.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">Δεν υπάρχουν μηνύματα</p>
        ) : (
          threadMessages.map((msg) => {
            const isOffice = msg.direction === 'OFFICE_TO_CLIENT';
            const senderName = msg.senderUserName || msg.senderContactName || 'Σύστημα';

            if (msg.isAutomated) {
              return (
                <div key={msg.id} className="flex justify-center">
                  <div className="bg-muted/50 rounded px-3 py-1.5 text-xs text-muted-foreground max-w-sm text-center">
                    {msg.body}
                    <span className="ml-2 text-xs opacity-60">
                      {new Date(msg.createdAt).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              );
            }

            return (
              <div key={msg.id} className={`flex ${isOffice ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs rounded-lg px-3 py-2 ${isOffice ? 'bg-blue-500 text-white' : 'bg-muted'}`}>
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <span className="text-xs font-medium">{senderName}</span>
                    <span className={`text-xs ${isOffice ? 'text-blue-100' : 'text-muted-foreground'}`}>
                      {new Date(msg.createdAt).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply */}
      <div className="border-t p-2 flex gap-2">
        <Textarea
          placeholder="Απάντηση... (Ctrl+Enter)"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          className="flex-1 resize-none text-sm"
        />
        <Button
          size="icon"
          className="self-end h-8 w-8"
          onClick={handleSend}
          disabled={!reply.trim() || sendMessage.isPending}
        >
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Requests Tab ────────────────────────────

function RequestsTab({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [reqPage, setReqPage] = useState(1);
  const [reqLimit, setReqLimit] = useState(10);
  const { data: requestsData } = useClientRequests({ clientId, page: reqPage, limit: reqLimit });
  const createRequest = useCreateClientRequest();
  const updateRequest = useUpdateClientRequest();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', dueDate: '' });

  const requests = requestsData?.data ?? [];

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    await createRequest.mutateAsync({
      clientId,
      title: form.title,
      description: form.description,
      dueDate: form.dueDate || undefined,
    });
    setForm({ title: '', description: '', dueDate: '' });
    setDialogOpen(false);
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-3">
        <CardTitle className="font-display text-base flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Αιτήματα προς πελάτη
        </CardTitle>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Νέο Αίτημα
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Νέο Αίτημα</DialogTitle>
              <DialogDescription>Δημιουργήστε αίτημα που θα εμφανιστεί στην πύλη του πελάτη</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Τίτλος *</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
              </div>
              <div>
                <Label>Περιγραφή *</Label>
                <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} required rows={3} />
              </div>
              <div>
                <Label>Προθεσμία</Label>
                <Input type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button type="submit" disabled={createRequest.isPending}>Αποστολή</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {requests.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Δεν υπάρχουν αιτήματα</p>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => {
              const s = req.status as string;
              return (
                <div key={req.id} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{req.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{req.description}</p>
                    </div>
                    <Badge variant={s === 'COMPLETED' ? 'success' : s === 'SEEN' ? 'secondary' : 'default'}>
                      {s === 'PENDING' ? 'Αναμονή' : s === 'SEEN' ? 'Αναγνώστηκε' : s === 'COMPLETED' ? 'Ολοκληρώθηκε' : s}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {req.dueDate && <span>Προθεσμία: {new Date(req.dueDate).toLocaleDateString('el-GR')}</span>}
                    <span>{new Date(req.createdAt).toLocaleDateString('el-GR')}</span>
                  </div>
                  {s !== 'COMPLETED' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateRequest.mutate({ id: req.id, data: { status: 'COMPLETED' } })}
                      disabled={updateRequest.isPending}
                    >
                      Ολοκλήρωση
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {requestsData && (
          <div className="pt-3 border-t mt-4">
            <Pagination
              page={requestsData.page}
              totalPages={requestsData.totalPages}
              total={requestsData.total}
              limit={reqLimit}
              onPageChange={setReqPage}
              onLimitChange={setReqLimit}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Activity Tab ────────────────────────────

function ActivityTab({ clientId }: { clientId: string }) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { data: activityData } = useActivity({ clientId, page, limit });
  const addNote = useAddNote();
  const [note, setNote] = useState('');

  const entries = activityData?.data ?? [];

  const actionLabels: Record<string, string> = {
    'client.created': 'Δημιουργία πελάτη',
    'client.updated': 'Ενημέρωση στοιχείων',
    'client.deleted': 'Διαγραφή πελάτη',
    'document.uploaded': 'Ανέβασμα εγγράφου',
    'document.status_changed': 'Αλλαγή κατάστασης εγγράφου',
    'document.extraction_confirmed': 'Επιβεβαίωση εξαγωγής AI',
    'deadline.updated': 'Ενημέρωση προθεσμίας',
    'deadline.extended': 'Παράταση προθεσμίας',
    'deadline.overdue': 'Εκπρόθεσμη υποχρέωση',
    'contact.invited': 'Πρόσκληση επαφής',
    'contact.deactivated': 'Απενεργοποίηση επαφής',
    'contact.link_regenerated': 'Ανανέωση συνδέσμου',
    'request.created': 'Δημιουργία αιτήματος',
    'request.updated': 'Ενημέρωση αιτήματος',
    'onboarding.started': 'Έναρξη onboarding',
    'onboarding.updated': 'Ενημέρωση onboarding',
    'note.added': 'Σημείωση',
  };

  async function handleAddNote(e: React.FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    await addNote.mutateAsync({ clientId, note: note.trim() });
    setNote('');
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-base flex items-center gap-2">
          <History className="h-4 w-4" />
          Ιστορικό δραστηριότητας
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add note form */}
        <form onSubmit={handleAddNote} className="flex gap-2">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Προσθήκη σημείωσης..."
            className="flex-1"
          />
          <Button type="submit" size="sm" disabled={addNote.isPending || !note.trim()}>
            Αποθήκευση
          </Button>
        </form>

        {/* Activity entries */}
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Δεν υπάρχει ιστορικό</p>
        ) : (
          <div className="timeline">
            {entries.map((entry) => {
              const isNote = entry.action === 'note.added';
              const meta = (entry.metadata ?? {}) as Record<string, unknown>;
              return (
                <div key={entry.id} className="timeline-item">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-medium">
                        {actionLabels[entry.action] ?? entry.action}
                      </p>
                      <time className="text-xs text-muted-foreground flex-shrink-0">
                        {new Date(entry.createdAt).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </time>
                    </div>
                    {isNote && meta.note ? (
                      <p className="text-sm text-muted-foreground mt-0.5 italic">{String(meta.note)}</p>
                    ) : null}
                    {!isNote && meta.filename ? (
                      <p className="text-xs text-muted-foreground">{String(meta.filename)}</p>
                    ) : null}
                    <p className="text-xs text-muted-foreground">
                      {entry.userName ?? entry.contactName ?? ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {activityData && (
          <div className="pt-3 border-t mt-4">
            <Pagination
              page={activityData.page}
              totalPages={activityData.totalPages}
              total={activityData.total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CreateContactDialog({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const createContact = useCreateContact();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    roleLabel: '',
    inviteChannel: InviteChannel.SMS,
    inviteTone: 'formal' as 'formal' | 'casual' | 'custom',
    customInviteMessage: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createContact.mutateAsync({
      clientId,
      data: {
        name: form.name,
        phone: form.phone || undefined,
        email: form.email || undefined,
        roleLabel: form.roleLabel || undefined,
        inviteChannel: form.inviteChannel,
        inviteTone: form.inviteTone,
        customInviteMessage: form.inviteTone === 'custom' ? form.customInviteMessage || undefined : undefined,
      },
    });
    onClose();
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Νέα Επαφή</DialogTitle>
        <DialogDescription>Προσθέστε στοιχεία επαφής και στείλτε πρόσκληση</DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label>Όνομα *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Τηλέφωνο</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+30 69..."
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
        </div>
        <div>
          <Label>Ρόλος</Label>
          <Input
            value={form.roleLabel}
            onChange={(e) => setForm((f) => ({ ...f, roleLabel: e.target.value }))}
            placeholder="π.χ. Ιδιοκτήτης, Γραμματέας"
          />
        </div>
        <div>
          <Label>Αποστολή πρόσκλησης μέσω</Label>
          <Select value={form.inviteChannel} onValueChange={(v) => setForm((f) => ({ ...f, inviteChannel: v as InviteChannel }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={InviteChannel.SMS}>SMS</SelectItem>
              <SelectItem value={InviteChannel.EMAIL}>Email</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Ύφος μηνύματος</Label>
          <Select value={form.inviteTone} onValueChange={(v) => setForm((f) => ({ ...f, inviteTone: v as 'formal' | 'casual' | 'custom' }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="formal">Επίσημο</SelectItem>
              <SelectItem value="casual">Φιλικό</SelectItem>
              <SelectItem value="custom">Προσαρμοσμένο</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.inviteTone === 'custom' && (
          <div>
            <Label>Προσαρμοσμένο μήνυμα</Label>
            <Textarea
              value={form.customInviteMessage}
              onChange={(e) => setForm((f) => ({ ...f, customInviteMessage: e.target.value }))}
              placeholder="Γράψτε το δικό σας μήνυμα πρόσκλησης..."
              rows={3}
              maxLength={500}
            />
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" disabled={createContact.isPending}>
            <Send className="h-4 w-4 mr-2" />
            {createContact.isPending ? t('common.loading') : 'Αποστολή'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// ─── Edit Client Dialog (B-1) ──────────────

function EditClientDialog({
  client,
  onClose,
}: {
  client: {
    id: string;
    businessName: string;
    afm: string;
    legalForm: string;
    accountingType: string;
    vatRegime: string;
    sector: string | null;
    kadCode: string | null;
    hasEmployees: boolean;
    hasRentalIncome: boolean;
    hasProperty: boolean;
    tags?: string[];
    notes: string | null;
  };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const updateClient = useUpdateClient();
  const [tagInput, setTagInput] = useState('');
  const [form, setForm] = useState({
    businessName: client.businessName,
    afm: client.afm,
    legalForm: client.legalForm,
    accountingType: client.accountingType,
    vatRegime: client.vatRegime,
    sector: client.sector ?? '',
    kadCode: client.kadCode ?? '',
    hasEmployees: client.hasEmployees,
    hasRentalIncome: client.hasRentalIncome,
    hasProperty: client.hasProperty,
    tags: client.tags ?? [],
    notes: client.notes ?? '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateClient.mutateAsync({
      id: client.id,
      data: {
        businessName: form.businessName,
        afm: form.afm,
        legalForm: form.legalForm as LegalForm,
        accountingType: form.accountingType as AccountingType,
        vatRegime: form.vatRegime as VatRegime,
        sector: form.sector || undefined,
        kadCode: form.kadCode || undefined,
        hasEmployees: form.hasEmployees,
        hasRentalIncome: form.hasRentalIncome,
        hasProperty: form.hasProperty,
        tags: form.tags,
        notes: form.notes || undefined,
      },
    });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Επεξεργασία Πελάτη</DialogTitle>
          <DialogDescription>Ενημέρωση στοιχείων πελάτη</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('clients.businessName')} *</Label>
              <Input value={form.businessName} onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))} required />
            </div>
            <div>
              <Label>{t('clients.afm')} *</Label>
              <Input value={form.afm} onChange={(e) => setForm((f) => ({ ...f, afm: e.target.value }))} required pattern="\d{9}" maxLength={9} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t('clients.legalForm')}</Label>
              <Select value={form.legalForm} onValueChange={(v) => setForm((f) => ({ ...f, legalForm: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(LegalForm).map((lf) => (
                    <SelectItem key={lf} value={lf}>{t(`clients.legalForms.${lf}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Λογιστικό Σύστημα</Label>
              <Select value={form.accountingType} onValueChange={(v) => setForm((f) => ({ ...f, accountingType: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(AccountingType).map((at) => (
                    <SelectItem key={at} value={at}>{at === 'SINGLE_ENTRY' ? 'Απλογραφικά' : 'Διπλογραφικά'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('clients.vatRegime')}</Label>
              <Select value={form.vatRegime} onValueChange={(v) => setForm((f) => ({ ...f, vatRegime: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.values(VatRegime).map((vr) => (
                    <SelectItem key={vr} value={vr}>{t(`clients.vatRegimes.${vr}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('clients.sector')}</Label>
              <Input value={form.sector} onChange={(e) => setForm((f) => ({ ...f, sector: e.target.value }))} />
            </div>
            <div>
              <Label>ΚΑΔ</Label>
              <Input value={form.kadCode} onChange={(e) => setForm((f) => ({ ...f, kadCode: e.target.value }))} />
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.hasEmployees} onChange={(e) => setForm((f) => ({ ...f, hasEmployees: e.target.checked }))} className="rounded" />
              Εργαζόμενοι
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.hasRentalIncome} onChange={(e) => setForm((f) => ({ ...f, hasRentalIncome: e.target.checked }))} className="rounded" />
              Ενοίκια
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.hasProperty} onChange={(e) => setForm((f) => ({ ...f, hasProperty: e.target.checked }))} className="rounded" />
              Ακίνητα
            </label>
          </div>

          <div>
            <Label>Ετικέτες</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs gap-1">
                  {tag}
                  <button
                    type="button"
                    className="ml-0.5 text-muted-foreground hover:text-foreground"
                    onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }))}
                  >
                    &times;
                  </button>
                </Badge>
              ))}
            </div>
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  const val = tagInput.trim().replace(/,/g, '');
                  if (val && !form.tags.includes(val)) {
                    setForm((f) => ({ ...f, tags: [...f.tags, val] }));
                  }
                  setTagInput('');
                }
              }}
              placeholder="Πληκτρολογήστε και πατήστε Enter..."
            />
          </div>

          <div>
            <Label>Σημειώσεις</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={3} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={updateClient.isPending}>
              {updateClient.isPending ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Staff Assignment Tab (B-7) ──────────

function StaffAssignmentTab({ clientId }: { clientId: string }) {
  const { data: usersData } = useUsers();
  const [assigning, setAssigning] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  const users = usersData?.data ?? [];

  // We don't have a dedicated "assignments for client" endpoint,
  // so we'll show all users and allow assign/unassign via the existing API
  const handleAssign = async () => {
    if (!selectedUserId) return;
    setAssigning(true);
    try {
      await api.post(`/users/${selectedUserId}/assignments`, { clientIds: [clientId] });
      setSelectedUserId('');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-3">
        <CardTitle className="font-display text-base flex items-center gap-2">
          <Users className="h-4 w-4" />
          Ανάθεση Ομάδας
        </CardTitle>
        <div className="flex items-center gap-2">
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Επιλογή χρήστη" />
            </SelectTrigger>
            <SelectContent>
              {users.filter((u) => u.isActive).map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleAssign} disabled={!selectedUserId || assigning}>
            <Plus className="h-4 w-4 mr-1" />
            Ανάθεση
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Όνομα</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Ρόλος</TableHead>
              <TableHead>Πελάτες</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.filter((u) => u.isActive).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Δεν υπάρχουν ενεργοί χρήστες
                </TableCell>
              </TableRow>
            ) : (
              users.filter((u) => u.isActive).map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{user.roleTemplateName ?? '—'}</Badge>
                  </TableCell>
                  <TableCell>{user.assignedClientCount}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ─── Client Documents Tab ──────────────────────

const DOC_STATUS_PILL_STYLES: Record<DocumentStatus, { active: string; idle: string }> = {
  [DocumentStatus.NEW]: {
    active: 'bg-gray-600 text-white border-gray-600',
    idle: 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700',
  },
  [DocumentStatus.REVIEWING]: {
    active: 'bg-yellow-500 text-white border-yellow-500',
    idle: 'border-yellow-300 text-yellow-600 hover:border-yellow-400 hover:text-yellow-700',
  },
  [DocumentStatus.REVIEWED]: {
    active: 'bg-blue-600 text-white border-blue-600',
    idle: 'border-blue-300 text-blue-500 hover:border-blue-400 hover:text-blue-700',
  },
  [DocumentStatus.POSTED]: {
    active: 'bg-green-600 text-white border-green-600',
    idle: 'border-green-300 text-green-600 hover:border-green-400 hover:text-green-700',
  },
};

const DOC_STATUS_LABELS: Record<DocumentStatus, string> = {
  [DocumentStatus.NEW]: 'Νέο',
  [DocumentStatus.REVIEWING]: 'Σε Εξέταση',
  [DocumentStatus.REVIEWED]: 'Εξετασμένο',
  [DocumentStatus.POSTED]: 'Καταχωρημένο',
};

function ClientDocumentsTab({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const { data: docsData } = useDocuments({ clientId, page, limit, sortBy: 'createdAt', sortOrder: 'desc' } as Record<string, unknown>);

  if (!docsData?.data.length) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">{t('documents.noDocuments')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {docsData.data.map((doc) => (
          <ClientDocumentRow key={doc.id} doc={doc} onSelect={() => setSelectedDocId(doc.id)} />
        ))}
      </div>
      <div className="mt-3">
        <Pagination
          page={docsData.page}
          totalPages={docsData.totalPages}
          total={docsData.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>
      {selectedDocId && (
        <ClientDocumentPreviewDialog docId={selectedDocId} onClose={() => setSelectedDocId(null)} />
      )}
    </>
  );
}

function ClientDocumentRow({ doc, onSelect }: { doc: TaxistryDocument; onSelect: () => void }) {
  const { t } = useTranslation();
  const updateStatus = useUpdateDocumentStatus();

  const statusVariant = doc.status === DocumentStatus.NEW ? 'default'
    : doc.status === DocumentStatus.REVIEWING ? 'warning'
    : doc.status === DocumentStatus.REVIEWED ? 'success'
    : 'success';

  const fileSizeMb = (doc.fileSizeBytes / 1024 / 1024).toFixed(1);
  const extraction = doc.aiExtraction;

  const isPdf = doc.mimeType === 'application/pdf';
  const isImage = doc.mimeType.startsWith('image/');
  const ext = doc.originalFilename.split('.').pop()?.toUpperCase() ?? '';
  const iconBg = isPdf ? 'bg-destructive/10 text-destructive' : isImage ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground';

  return (
    <Card className="hover:border-border rounded-2xl border-border">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`${iconBg} p-2.5 rounded-xl shrink-0 flex flex-col items-center justify-center min-w-[44px]`}>
            <FileText className="h-5 w-5" />
            <span className="text-xs font-data font-semibold mt-0.5">{ext}</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <button onClick={onSelect} className="font-medium text-sm hover:underline truncate text-left">
                {doc.originalFilename}
              </button>
              <Badge variant={statusVariant} className="shrink-0">
                {t(`documents.status.${doc.status}`)}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{new Date(doc.createdAt).toLocaleDateString('el-GR')}</span>
              <span>&middot;</span>
              <span className="font-data">{fileSizeMb} MB</span>
              <span>&middot;</span>
              <span>{doc.uploadSource === 'PORTAL' ? 'Portal' : doc.uploadSource === 'EMAIL' ? 'Email' : 'Dashboard'}</span>
              {doc.aiDocumentType ? (
                <>
                  <span>&middot;</span>
                  <span>{DOCUMENT_TYPE_LABELS[doc.aiDocumentType] ?? doc.aiDocumentType}</span>
                </>
              ) : null}
              {doc.aiExpenseCategory ? (
                <>
                  <span>&middot;</span>
                  <span>{EXPENSE_CATEGORY_LABELS[doc.aiExpenseCategory] ?? doc.aiExpenseCategory}</span>
                </>
              ) : null}
            </div>
            {extraction && (
              <div className="flex items-center gap-4 mt-2 text-xs">
                {extraction.issuerName && (
                  <span>{t('documents.supplier')}: <strong>{extraction.issuerName}</strong></span>
                )}
                {extraction.totalAmount != null && (
                  <span>{t('documents.amount')}: <strong>{extraction.totalAmount.toFixed(2)}€</strong></span>
                )}
                {extraction.date && (
                  <span>{t('documents.date')}: <strong>{extraction.date}</strong></span>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={onSelect} title="Προβολή">
              <Eye className="h-4 w-4" />
            </Button>
            {doc.status === DocumentStatus.NEW && (
              <Button
                variant="ghost"
                size="icon"
                title="Σε εξέταση"
                onClick={() => updateStatus.mutate({ id: doc.id, status: DocumentStatus.REVIEWING })}
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ClientDocumentPreviewDialog({ docId, onClose }: { docId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: doc, isLoading } = useDocument(docId);
  const updateStatus = useUpdateDocumentStatus();
  const confirmExtraction = useConfirmExtraction();
  const reprocess = useReprocessDocument();
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Record<string, string | number | null>>({});

  const startEditing = () => {
    if (!doc?.aiExtraction) return;
    setEditData({
      issuerAfm: doc.aiExtraction.issuerAfm ?? '',
      issuerName: doc.aiExtraction.issuerName ?? '',
      invoiceNumber: doc.aiExtraction.invoiceNumber ?? '',
      date: doc.aiExtraction.date ?? '',
      netAmount: doc.aiExtraction.netAmount,
      vatAmount: doc.aiExtraction.vatAmount,
      totalAmount: doc.aiExtraction.totalAmount,
      vatRate: doc.aiExtraction.vatRate,
    });
    setEditing(true);
  };

  const handleConfirm = () => {
    if (!doc) return;
    const extraction: Record<string, unknown> = {
      ...(doc.aiExtraction ?? {}),
      issuerAfm: editData.issuerAfm || null,
      issuerName: editData.issuerName || null,
      invoiceNumber: editData.invoiceNumber || null,
      date: editData.date || null,
      netAmount: editData.netAmount != null && editData.netAmount !== '' ? Number(editData.netAmount) : null,
      vatAmount: editData.vatAmount != null && editData.vatAmount !== '' ? Number(editData.vatAmount) : null,
      totalAmount: editData.totalAmount != null && editData.totalAmount !== '' ? Number(editData.totalAmount) : null,
      vatRate: editData.vatRate != null && editData.vatRate !== '' ? Number(editData.vatRate) : null,
    };
    confirmExtraction.mutate(
      { id: doc.id, extraction },
      { onSuccess: () => setEditing(false) },
    );
  };

  const updateField = (field: string, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{doc?.originalFilename ?? t('common.loading')}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Skeleton className="h-96 w-full" />
          </div>
        ) : doc ? (
          <div className="flex-1 grid grid-cols-2 gap-4 overflow-hidden">
            <div className="border border-border rounded-lg overflow-auto bg-muted/30">
              {doc.mimeType.startsWith('image/') ? (
                <img src={doc.signedUrl} alt={doc.originalFilename} className="w-full object-contain" />
              ) : doc.mimeType === 'application/pdf' ? (
                <iframe src={doc.signedUrl} className="w-full h-full min-h-[400px]" title="PDF preview" />
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  Δεν υποστηρίζεται η προεπισκόπηση αυτού του τύπου αρχείου
                </div>
              )}
            </div>

            <div className="overflow-auto space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm">{t('documents.aiExtraction')}</h3>
                  {doc.aiExtraction && !editing && !doc.confirmedExtraction && (
                    <Button variant="outline" size="sm" onClick={startEditing}>
                      Επεξεργασία
                    </Button>
                  )}
                  {doc.confirmedExtraction && (
                    <Badge variant="success">Επιβεβαιωμένο</Badge>
                  )}
                </div>
                {doc.aiExtraction ? (
                  editing ? (
                    <div className="space-y-3 text-sm">
                      <DocEditableField label={t('documents.supplier')} value={String(editData.issuerName ?? '')} onChange={(v) => updateField('issuerName', v)} />
                      <DocEditableField label="ΑΦΜ Εκδότη" value={String(editData.issuerAfm ?? '')} onChange={(v) => updateField('issuerAfm', v)} />
                      <DocEditableField label={t('documents.invoiceNumber')} value={String(editData.invoiceNumber ?? '')} onChange={(v) => updateField('invoiceNumber', v)} />
                      <DocEditableField label={t('documents.date')} value={String(editData.date ?? '')} onChange={(v) => updateField('date', v)} type="date" />
                      <DocEditableField label="Καθαρή Αξία (€)" value={editData.netAmount != null ? String(editData.netAmount) : ''} onChange={(v) => updateField('netAmount', v)} type="number" />
                      <DocEditableField label="ΦΠΑ (€)" value={editData.vatAmount != null ? String(editData.vatAmount) : ''} onChange={(v) => updateField('vatAmount', v)} type="number" />
                      <DocEditableField label={`${t('documents.amount')} (€)`} value={editData.totalAmount != null ? String(editData.totalAmount) : ''} onChange={(v) => updateField('totalAmount', v)} type="number" />
                      <DocEditableField label="ΦΠΑ %" value={editData.vatRate != null ? String(editData.vatRate) : ''} onChange={(v) => updateField('vatRate', v)} type="number" />
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" onClick={handleConfirm} disabled={confirmExtraction.isPending}>
                          {confirmExtraction.isPending ? 'Αποθήκευση...' : 'Επιβεβαίωση'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                          Ακύρωση
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      {(() => {
                        const data = doc.confirmedExtraction ?? doc.aiExtraction;
                        return (
                          <>
                            <DocExtractionField label={t('documents.supplier')} value={data.issuerName} />
                            <DocExtractionField label="ΑΦΜ Εκδότη" value={data.issuerAfm} />
                            <DocExtractionField label={t('documents.invoiceNumber')} value={data.invoiceNumber} />
                            <DocExtractionField label={t('documents.date')} value={data.date} />
                            <DocExtractionField label="Καθαρή Αξία" value={data.netAmount != null ? `${data.netAmount.toFixed(2)}€` : null} />
                            <DocExtractionField label="ΦΠΑ" value={data.vatAmount != null ? `${data.vatAmount.toFixed(2)}€` : null} />
                            <DocExtractionField label={t('documents.amount')} value={data.totalAmount != null ? `${data.totalAmount.toFixed(2)}€` : null} />
                            <DocExtractionField label="ΦΠΑ %" value={data.vatRate} />
                            <DocExtractionField label="Τύπος Εγγράφου" value={doc.aiDocumentType ? (DOCUMENT_TYPE_LABELS[doc.aiDocumentType] ?? doc.aiDocumentType) : null} />
                            <DocExtractionField label="Κατηγορία Δαπάνης" value={doc.aiExpenseCategory ? (EXPENSE_CATEGORY_LABELS[doc.aiExpenseCategory] ?? doc.aiExpenseCategory) : null} />
                          </>
                        );
                      })()}
                      {doc.aiConfidence != null && (
                        <div className="pt-2">
                          <Badge variant={doc.aiConfidence > 0.8 ? 'success' : doc.aiConfidence > 0.5 ? 'warning' : 'error'}>
                            Βεβαιότητα: {(doc.aiConfidence * 100).toFixed(0)}%
                          </Badge>
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground">Δεν έχει γίνει AI εξαγωγή ακόμα</p>
                )}
              </div>

              <div className="pt-4 border-t space-y-3">
                <h3 className="font-medium text-sm">Κατάσταση</h3>
                <DocStatusPills
                  currentStatus={doc.status as DocumentStatus}
                  onChangeStatus={(status) => updateStatus.mutate({ id: doc.id, status })}
                  isPending={updateStatus.isPending}
                  isSuccess={updateStatus.isSuccess}
                />
                {!doc.confirmedExtraction && doc.status !== DocumentStatus.POSTED && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reprocess.mutate(doc.id)}
                    disabled={reprocess.isPending}
                  >
                    <RotateCw className={`h-4 w-4 mr-1.5 ${reprocess.isPending ? 'animate-spin' : ''}`} />
                    {reprocess.isPending ? 'Επεξεργασία...' : 'Επανεπεξεργασία AI'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DocStatusPills({
  currentStatus,
  onChangeStatus,
  isPending,
  isSuccess,
}: {
  currentStatus: DocumentStatus;
  onChangeStatus: (status: DocumentStatus) => void;
  isPending: boolean;
  isSuccess: boolean;
}) {
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1200);
      return () => clearTimeout(t);
    }
  }, [isSuccess]);

  const allStatuses = [DocumentStatus.NEW, DocumentStatus.REVIEWING, DocumentStatus.REVIEWED, DocumentStatus.POSTED];

  return (
    <div className="flex items-center gap-1">
      {allStatuses.map((status) => {
        const isActive = status === currentStatus;
        const styles = DOC_STATUS_PILL_STYLES[status];
        return (
          <button
            key={status}
            disabled={isPending || isActive}
            onClick={() => onChangeStatus(status)}
            className={`
              px-3 py-1.5 text-xs font-medium rounded-full border transition-all duration-200
              inline-flex items-center gap-1
              disabled:cursor-default
              ${isActive ? styles.active : styles.idle}
              ${isActive && flash ? 'ring-2 ring-offset-1 ring-green-400 animate-pulse' : ''}
            `}
          >
            {isActive && <Check className="h-3 w-3" />}
            {DOC_STATUS_LABELS[status]}
          </button>
        );
      })}
    </div>
  );
}

function DocExtractionField({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null) return null;
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function DocEditableField({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'date';
}) {
  return (
    <div className="grid grid-cols-[1fr_1.5fr] gap-2 items-center">
      <label className="text-muted-foreground text-xs">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-sm"
        step={type === 'number' ? '0.01' : undefined}
      />
    </div>
  );
}
